import { useContext, useEffect, useState } from "react";
import {addEventListener} from "@firefly/fly-hook";
import PaymentClientContext from "./context/PaymentClientContext";
import executePayment from "./executePayment2";
import { getStoragePayments, saveStoragePayments } from "./storage";
import {
  PaymentExecutionOptions,
  PaymentExecutionState,
  PaymentMethod,
  Payment,
  SuspensePayment,
} from "./typing";
import useSuspensePaymentStorage from "./useSuspensePaymentStorage";

export interface UsePaymentType {
  execute: (payment: Payment, method: PaymentMethod, args: Record<string, any>, options?: PaymentExecutionOptions) => Promise<PaymentExecutionState>;
  suspendedPayment: SuspensePayment | null;
  cancelSuspend: () => void;
}

/**
 * 新版 usePayment2 准备替换旧版
 * @param keys
 */
function usePayment(keys: any[] = []): UsePaymentType {
  const [suspendedPayment, _setSuspendedPayment] = useState<SuspensePayment | null>(
    null,
  );
  const paymentIdentifier = JSON.stringify(keys);
  const client = useContext(PaymentClientContext);
  const {
   defaultOptions: defaultOptionsFn
  } = client;
  const {
    query,
    remove
  } = useSuspensePaymentStorage();
  useEffect(() => {
    function pullPending() {
      const payments: SuspensePayment[] = query();
      const payment = payments.find((item) => {
        return item.identifier === paymentIdentifier;
      });
      if (payment) {
        remove(payment.identifier);
        _setSuspendedPayment(payment);
      }
    }
    pullPending();
    const listener = addEventListener(document, "visibilitychange", () => {
      const state = document.visibilityState;
      if (state === "visible") {
        //更新状态
        pullPending();
      }
    });
    return () => {
      listener.remove();
    };
  }, [paymentIdentifier, query, remove]);

  function setSuspended(value: Payment) {
    //等待支付
    _setSuspendedPayment({
      identifier: paymentIdentifier,
      id: value.id,
      outId: value.outId,
    });
  }
  /**
   * 挂起支付订单，下次回调回来进行确认 是否支付
   * @param value
   * 部分浏览器快速更新url 后再跳转，不生效，用session进行存储
   */
  function suspense(value?: Payment) {
    _setSuspendedPayment(null);

    const storagePayments = getStoragePayments();
    const payments = storagePayments.filter((item) => {
      return item.identifier !== paymentIdentifier;
    });

    if (value) {
      payments.unshift({
        identifier: paymentIdentifier,
        id: value.id,
        outId: value.outId,
      });
    }
    saveStoragePayments(payments);
  }

  // const [confirm, setConfirm] = useState<boolean>();
  // useEffect(() => {
  //   if (pendingPayment) {
  //     setConfirm(true);
  //   }
  // }, [pendingPayment]);


  function execute(
    payment: Payment,
    method: PaymentMethod,
    args: Record<string, any>,
    options: PaymentExecutionOptions = {},
  ): Promise<PaymentExecutionState> {
    const paymentId = payment.id;
    const defaultOptions = defaultOptionsFn(method);
    options = Object.assign(defaultOptions, options);
    return executePayment(client, paymentId, method, args, options).then(
      (status: PaymentExecutionState) => {
        if (status === "suspend") {
          suspense(payment);
        }
        if (status === "confirm") {
          setSuspended(payment);
        }
        return status;
      },
    );
  }
  function cancelSuspend() {
    _setSuspendedPayment(null);
  }
  return {
    suspendedPayment,
    cancelSuspend,
    execute: execute
  };
}


export default usePayment;
